import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Lock, Mail, User, AlertCircle, CheckCircle2, ServerCog } from "lucide-react";
import api from "../lib/api";

export default function SetupPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { fetchMe } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }
    if (password.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }
    setLoading(true);
    try {
      const res = await api.post("/auth/setup", { email, password, full_name: fullName });
      localStorage.setItem("stancor_token", res.data.token);
      localStorage.setItem("stancor_user", JSON.stringify(res.data.user));
      await fetchMe();
      navigate("/setup/configure");
    } catch (err) {
      setError(err.response?.data?.detail || "Setup failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="setup-page">
      <div className="hidden lg:flex lg:w-1/2 bg-[#1a1a1f] relative overflow-hidden flex-col justify-center items-center p-12">
        <div className="absolute inset-0 opacity-5"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='m36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
        />
        <div className="relative z-10 text-center space-y-6">
          <div className="w-20 h-20 rounded-2xl bg-[#C41E3A]/20 flex items-center justify-center mx-auto">
            <ServerCog className="w-10 h-10 text-[#C41E3A]" />
          </div>
          <h2 className="text-white text-3xl font-bold leading-tight">
            Welcome to<br />
            <span className="text-[#C41E3A]">SteelOne ERP</span>
          </h2>
          <p className="text-white/50 text-sm max-w-sm leading-relaxed">
            Set up your administrator account to get started. This is a one-time setup for your new database.
          </p>
          <div className="flex items-center gap-2 text-white/30 text-xs">
            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
            <span>Database connected</span>
          </div>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center bg-background p-8">
        <div className="w-full max-w-sm space-y-8">
          <div className="lg:hidden flex items-center gap-3 mb-4">
            <div className="w-9 h-9 rounded-md bg-primary flex items-center justify-center text-primary-foreground font-bold text-sm">ST</div>
            <span className="text-lg font-bold tracking-tight">SteelOne ERP</span>
          </div>

          <div>
            <h1 className="text-2xl font-bold tracking-tight" data-testid="setup-heading">Create Admin Account</h1>
            <p className="text-sm text-muted-foreground mt-1">Set up your administrator credentials for this database</p>
          </div>

          {error && (
            <div className="flex items-center gap-2 text-sm text-destructive bg-destructive/10 p-3 rounded-md" data-testid="setup-error">
              <AlertCircle className="w-4 h-4 shrink-0" />
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4" data-testid="setup-form">
            <div className="space-y-2">
              <Label htmlFor="fullName">Full Name</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input id="fullName" value={fullName} onChange={e => setFullName(e.target.value)} placeholder="System Administrator" className="pl-10 h-11" data-testid="setup-name-input" />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Admin Email <span className="text-destructive">*</span></Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input id="email" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="admin@yourcompany.com" className="pl-10 h-11" data-testid="setup-email-input" required />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password <span className="text-destructive">*</span></Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input id="password" type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Min 6 characters" className="pl-10 h-11" data-testid="setup-password-input" required />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm Password <span className="text-destructive">*</span></Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input id="confirmPassword" type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder="Re-enter password" className="pl-10 h-11" data-testid="setup-confirm-input" required />
              </div>
            </div>

            <Button type="submit" className="w-full h-11 rounded-md font-semibold uppercase tracking-wider text-sm" disabled={loading} data-testid="setup-submit-btn">
              {loading ? "Creating Account..." : "Create Admin Account"}
            </Button>
          </form>

          <p className="text-[11px] text-center text-muted-foreground">This account will have full administrative privileges</p>
        </div>
      </div>
    </div>
  );
}
